// DS — segment (artboard 02 systemu 06, sesja 08): 2–4 pozycje, JEDNA wybrana.
//
// Stoi nad klasami `.seg` / `.seg button[aria-pressed="true"]` z ds.css — 06 dało
// wyłącznie CSS, ta paczka daje komponent, żeby trzy miejsca (język PL · EN w menu
// i w Ustawieniach, tryb paczek klipów) nie kopiowały tych samych przycisków.
//
// Klawiatura jak w grupie radiowej: strzałki / Home / End przenoszą wybór i fokus,
// tabulator wchodzi tylko na pozycję wybraną (roving tabindex). Stan niesie
// `aria-pressed`, więc czytnik i CSS czytają to samo.

import type { KeyboardEvent, ReactNode } from 'react'
import { useRef } from 'react'

export interface SegmentOption<T extends string> {
  value: T
  label: ReactNode
  disabled?: boolean
  /** Uchwyt testowy przycisku pozycji. */
  testId?: string
}

export interface SegmentProps<T extends string> {
  options: SegmentOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Nazwa grupy dla czytnika — obowiązkowa, bo etykieta wizualna stoi zwykle obok. */
  'aria-label': string
  /**
   * CAŁA grupa nieaktywna — i to naprawdę, nie samym kolorem (paczka N2, D2).
   *
   * `aria-disabled`, nie `disabled`: kontrolka nieaktywna ZACHOWUJE wartość i ma ją
   * ogłaszać („30 dni” dalej jest wybranym zakresem, tylko nie ma czego liczyć), więc
   * przyciski zostają w drzewie dostępności. Wypadają za to z kolejności tabulacji,
   * a klik, Enter, spacja i strzałki nic nie robią — do tej paczki grupa dostawała
   * wyłącznie klasę CSS i pusty `onChange`, więc wyglądała na wyłączoną, a strzałki
   * i tak przenosiły fokus, zaznaczenie nie szło za nimi i nic tego nie tłumaczyło.
   */
  disabled?: boolean
  /** 36 (`sm`) w wierszu menu; domyślny w treści. */
  size?: 'md' | 'sm'
  className?: string
  'data-testid'?: string
}

export function Segment<T extends string>({ options, value, onChange, disabled, size = 'md', className = '', ...rest }: SegmentProps<T>) {
  const ref = useRef<HTMLDivElement>(null)

  const move = (from: number, dir: 1 | -1 | 'first' | 'last') => {
    if (disabled) return
    const enabled = options.filter((o) => !o.disabled)
    if (enabled.length === 0) return
    let next: SegmentOption<T>
    if (dir === 'first') next = enabled[0]
    else if (dir === 'last') next = enabled[enabled.length - 1]
    else {
      const i = enabled.findIndex((o) => o.value === options[from].value)
      next = enabled[(i + dir + enabled.length) % enabled.length]
    }
    onChange(next.value)
    ref.current?.querySelector<HTMLButtonElement>(`button[data-value="${next.value}"]`)?.focus()
  }

  const onKeyDown = (i: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(i, 1) }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(i, -1) }
    else if (e.key === 'Home') { e.preventDefault(); move(i, 'first') }
    else if (e.key === 'End') { e.preventDefault(); move(i, 'last') }
  }

  // Roving tabindex. With no selection yet (a Short's visibility has no default on
  // purpose) the first enabled option carries the tab stop; otherwise every option is
  // tabIndex -1 and the whole group is unreachable from the keyboard.
  const selectedIndex = options.findIndex((o) => o.value === value)
  const rovingIndex = disabled ? -1 : selectedIndex >= 0 ? selectedIndex : options.findIndex((o) => !o.disabled)

  return (
    <div
      ref={ref}
      className={['seg', size === 'sm' && 'seg-sm', className].filter(Boolean).join(' ')}
      role="group"
      aria-label={rest['aria-label']}
      data-testid={rest['data-testid']}
    >
      {options.map((o, i) => {
        const selected = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            data-value={o.value}
            data-testid={o.testId}
            aria-pressed={selected}
            aria-disabled={disabled || undefined}
            disabled={o.disabled}
            tabIndex={i === rovingIndex ? 0 : -1}
            onClick={() => { if (!disabled && !selected) onChange(o.value) }}
            onKeyDown={onKeyDown(i)}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export default Segment
